import { MinimalUserDto, SaveAttachmentDto } from '@issuemanager/api-client';
import { ControlledFileUploader } from '@issuemanager/apps/client/components';
import { useAttachmentsContext } from '@issuemanager/apps/client/contexts';
import {
  useAllMutations,
  useMonitorApproveActionSubmit,
} from '@issuemanager/apps/client/hooks';
import { useApproveAction } from '@issuemanager/client/features/issues';
import { IssuePermissionsEnum } from '@issuemanager/constants';
import { useEventCallback } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIssueOutletContext } from '../../../hooks/useIssueOutletContext';
import { LayoutPanel } from '../layout';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  actionId: string;
  actionTitle: string;
};

type FormValues = {
  attachments: (SaveAttachmentDto & {
    addedBy: MinimalUserDto;
    createdAt: string;
  })[];
};

export function ActionApprovalPanel({
  onClose,
  isOpen,
  actionId,
  actionTitle,
}: Props) {
  const { t } = useTranslation('action');
  const { enqueueSnackbar } = useSnackbar();
  const { code: issueCode } = useIssueOutletContext();
  const { isLoading: isUploadAttachmentsLoading } = useAttachmentsContext();

  const approveAction = useApproveAction({
    issueCode,
    onSuccess: () =>
      enqueueSnackbar(t('manage.approve.success'), {
        variant: 'success',
      }),
  });

  const monitorApproveActionSubmit = useMonitorApproveActionSubmit();

  const form = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      attachments: [],
    },
  });

  useAllMutations({
    mutations: [approveAction],
    onStart: () => {
      monitorApproveActionSubmit.start();
    },
    onSuccess: () => {
      monitorApproveActionSubmit.stop();
    },
    onError: () => {
      monitorApproveActionSubmit.error();
    },
  });

  const handleSubmit = useEventCallback(() => {
    form.handleSubmit(onSubmit)();
  });

  const onSubmit = async ({ attachments }: FormValues) => {
    await approveAction.mutateAsync({ id: actionId, attachments });
    onClose();
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <LayoutPanel
      isOpen={isOpen}
      onCancel={handleCancel}
      title={t('manage.approve.title')}
      description={t('manage.approve.description', {
        action: actionTitle,
      })}
      actionButton={{
        disabled: approveAction.isPending || isUploadAttachmentsLoading,
        'data-testid': 'approve-footer-button',
      }}
      cancelButton={{
        disabled: isUploadAttachmentsLoading,
      }}
      actionButtonText={
        approveAction.isPending
          ? t('manage.approve.approving')
          : t('manage.approve.accept')
      }
      isLoading={approveAction.isPending}
      onSend={handleSubmit}
      content={
        <FormProvider {...form}>
          <form data-testid='form-approve-action'>
            <ControlledFileUploader
              control={form.control}
              name='attachments'
              disabled={approveAction.isPending}
              permissions={[IssuePermissionsEnum.IsActionApprover]}
            />
          </form>
        </FormProvider>
      }
    />
  );
}
